/**
 * Shared slide rendering utilities.
 * Used by both SlideBuilder (live preview) and SlideManager (Update All).
 */
import React from 'react';
import html2canvas from 'html2canvas';
import { ArabicShaper } from 'arabic-persian-reshaper';
import { createRoot } from 'react-dom/client';
import { injectAnimationStyles, computeLayerAnim, hasAnyAnimation } from './animationCatalog';

// Side-effect: inject the @keyframes stylesheet on first module import. Safe
// to call repeatedly; the function no-ops after the first call.
if (typeof document !== 'undefined') injectAnimationStyles();

// ── Arabic pre-shaping for canvas capture ─────────────────────────────────────
// html2canvas re-implements text rendering on canvas and DOES NOT do Arabic
// shaping (joining). So even though the live DOM renders correctly, the JPEG
// capture comes out with letters disconnected.
//
// Workaround: convert standard Arabic (U+0600-U+06FF) into Unicode Presentation
// Forms-B (U+FE70-U+FEFF). Each codepoint in that block is a pre-composed glyph
// already in its proper joined shape (initial/medial/final/isolated). Painting
// them with canvas2d.fillText doesn't need any shaping logic — they look correct.
//
// Applied ONLY to data passed to the off-screen render (renderSlideToCanvas).
// The live DOM preview keeps the original strings so the browser does proper
// shaping there and copy-paste from the page yields normal Arabic text.
const _hasArabic = (s) => typeof s === 'string' && /[؀-ۿ]/.test(s);
function shapeArabic(s) {
  if (!_hasArabic(s)) return s;
  try { return ArabicShaper.convertArabic(s); } catch { return s; }
}
function preShapeMenuData(menuData) {
  if (!menuData) return menuData;
  return {
    ...menuData,
    allItems: (menuData.allItems || []).map(item => ({
      ...item,
      name:           shapeArabic(item.name),
      name_ar:        shapeArabic(item.name_ar),
      description:    shapeArabic(item.description),
      description_ar: shapeArabic(item.description_ar),
      variants: (item.variants || []).map(v => ({
        ...v,
        name:    shapeArabic(v.name),
        name_ar: shapeArabic(v.name_ar),
      })),
    })),
    categories: (menuData.categories || []).map(c => ({
      ...c,
      name:    shapeArabic(c.name),
      name_ar: shapeArabic(c.name_ar),
    })),
  };
}
function preShapeSlideData(slideData) {
  if (!slideData) return slideData;
  const shapeLayers = (arr) => Array.isArray(arr) ? arr.map(l => ({
    ...l,
    text:    shapeArabic(l.text),
    text_en: shapeArabic(l.text_en),
    text_ar: shapeArabic(l.text_ar),
  })) : arr;
  return {
    ...slideData,
    zones: Array.isArray(slideData.zones) ? slideData.zones.map(z => ({
      ...z,
      name:    shapeArabic(z.name),
      name_ar: shapeArabic(z.name_ar),
      text_layers: shapeLayers(z.text_layers),
    })) : slideData.zones,
    text_layers: shapeLayers(slideData.text_layers),
  };
}
function preShapeRestaurant(r) {
  if (!r) return r;
  return { ...r, name: shapeArabic(r.name), name_ar: shapeArabic(r.name_ar) };
}

// ── Design dimensions ─────────────────────────────────────────────────────────
// DESIGN provides the legacy default: preview at 960×540 (H) / 540×960 (V).
// Combined with `scale: 2` in html2canvas, this exports at 1920×1080 / 1080×1920.
// Slides can now carry per-row `width_px` / `height_px` overrides — when set,
// the preview becomes (width_px/2, height_px/2) and the export hits the exact
// requested resolution. Defaults stay the same when no override is provided.
export const DESIGN = { H: { w: 960, h: 540 }, V: { w: 540, h: 960 } };

// Helper: compute preview dim from a slide. Half of the output resolution so
// `scale: 2` in html2canvas produces the user's requested pixel size.
export function dimFor(slideOrOrientation) {
  if (slideOrOrientation && typeof slideOrOrientation === 'object'
      && slideOrOrientation.width_px && slideOrOrientation.height_px) {
    return { w: slideOrOrientation.width_px / 2, h: slideOrOrientation.height_px / 2 };
  }
  const ori = (typeof slideOrOrientation === 'string') ? slideOrOrientation : 'H';
  return DESIGN[ori] || DESIGN.H;
}

// ── Pure helpers ──────────────────────────────────────────────────────────────
export function isLightBg(hex) {
  const h = (hex || '').replace('#', '');
  const p = (s) => parseInt(s, 16);
  let r, g, b;
  if (h.length === 3) { r = p(h[0]+h[0]); g = p(h[1]+h[1]); b = p(h[2]+h[2]); }
  else if (h.length === 6) { r = p(h.slice(0,2)); g = p(h.slice(2,4)); b = p(h.slice(4,6)); }
  else return false;
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
}

export function makeTokens(bgColor, accentColor) {
  const light = isLightBg(bgColor);
  return {
    light,
    text:      light ? '#111111' : '#ffffff',
    textFaded: light ? '#555555' : 'rgba(255,255,255,0.42)',
    rowBg:     light ? '#ffffff' : 'rgba(255,255,255,0.06)',
    imgBg:     light ? '#f0f0f0' : 'rgba(0,0,0,0.25)',
  };
}

export function fmtP(p) {
  const n = parseFloat(p);
  return isNaN(n) ? (p ?? '') : n % 1 === 0 ? String(Math.round(n)) : String(n);
}

export function tr(en, ar, isAr) { return isAr ? (ar || en || '') : (en || ar || ''); }

// Render one positioned text-layer div. Coordinate origin is the parent's top-left
// (zone-relative when used inside ZonePreview, slide-relative inside SlidePreview).
export function TextLayerDiv({ layer, font, isAr, keyId, currentTime, slideDuration }) {
  const layerText = (layer.text != null && layer.text !== '')
    ? layer.text
    : tr(layer.text_en, layer.text_ar, isAr);
  if (!layerText) return null;
  const widthVal = typeof layer.w === 'number' ? layer.w
    : (!layer.w || layer.w === '100%') ? '100%'
    : (parseFloat(layer.w) || '100%');
  const heightVal = typeof layer.h === 'number' && layer.h > 0 ? layer.h : 'auto';
  const hasArabic = /[؀-ۿ]/.test(layerText);
  const flexCenter = typeof heightVal === 'number';
  // Phase C MVP (2026-05-16): when the editor pane drives a playhead via
  // currentTime, compute the per-layer animation state. Without currentTime
  // (e.g., isExport canvas capture, list-view thumbnails), render statically
  // with the layer's natural opacity — the compose pipeline gates time-based
  // visibility on its own via ffmpeg enable filters (Phase C-β).
  const anim = (currentTime != null && slideDuration)
    ? computeLayerAnim(layer, currentTime, slideDuration)
    : null;
  if (anim && !anim.visible) return null;
  const baseOpacity = (layer.opacity ?? 100) / 100;
  const effectiveOpacity = anim ? anim.opacity : baseOpacity;
  return (
    <div key={keyId} style={{
      position: 'absolute',
      left: layer.x ?? 0,
      top: layer.y ?? 0,
      width: widthVal,
      height: heightVal,
      display: flexCenter ? 'flex' : 'block',
      alignItems: flexCenter ? 'center' : undefined,
      justifyContent: flexCenter
        ? (layer.align === 'right' ? 'flex-end' : layer.align === 'left' ? 'flex-start' : 'center')
        : undefined,
      fontSize: layer.font_size || 24,
      fontWeight: layer.font_weight || 'bold',
      fontStyle: layer.font_style || 'normal',
      fontFamily: layer.font_family || (hasArabic ? 'Cairo, sans-serif' : (font || 'inherit')),
      color: layer.color || '#000000',
      textAlign: layer.align || 'center',
      opacity: effectiveOpacity,
      // CSS custom property read by the @keyframes animations so a faded layer
      // stays faded after the fade-in completes.
      '--layer-opacity': effectiveOpacity,
      animation: (anim && anim.animation) || undefined,
      zIndex: 20,
      pointerEvents: 'none',
      lineHeight: 1.25,
      whiteSpace: hasArabic ? 'normal' : 'pre-wrap',
      wordBreak: hasArabic ? 'normal' : 'break-word',
      overflowWrap: hasArabic ? 'break-word' : undefined,
      overflow: 'hidden',
      direction: hasArabic ? 'rtl' : 'ltr',
      letterSpacing: hasArabic ? 0 : (layer.letter_spacing ?? 0),
      textShadow: layer.text_shadow || 'none',
      boxSizing: 'border-box',
    }}>
      <span style={{ width: '100%' }}>{layerText}</span>
    </div>
  );
}

export function adjustZones(existing, cols, rows) {
  const total = Math.max(1, cols) * Math.max(1, rows);
  const EMPTY = (idx) => ({
    id: `z${idx + 1}_${Date.now() + idx}`,
    name: '', name_ar: '', item_ids: [], cols: 1, rows: 4,
    display_mode: 'list',
    show_name: true, show_photos: true, show_price: true, show_desc: false,
    name_font: '', price_font: '', desc_font: '', header_font: '',
    name_font_family: '',
    placeholder_opacity: 100,
    show_placeholder: true,
  });
  return Array.from({ length: total }, (_, i) => existing[i] || EMPTY(i));
}

// Convert grid-mode zones to free-mode by computing each zone's x/y/w/h from the grid math.
// Compute each cell's left/right edges by rounding boundaries (not by rounding-then-adding
// width separately) so the right column's right edge lands exactly on dim.w — otherwise
// rounding errors can push the last zone past the slide edge or leave a 1-px gap.
export function gridToFreeZones(zones, zone_cols, zone_rows, dim) {
  const cols = Math.max(1, zone_cols || 1);
  const rows = Math.max(1, zone_rows || 1);
  return zones.map((z, idx) => {
    const c = idx % cols;
    const r = Math.floor(idx / cols);
    const x0 = Math.round((c * dim.w) / cols);
    const x1 = Math.round(((c + 1) * dim.w) / cols);
    const y0 = Math.round((r * dim.h) / rows);
    const y1 = Math.round(((r + 1) * dim.h) / rows);
    return {
      ...z,
      x: x0,
      y: y0,
      w: x1 - x0,
      h: y1 - y0,
      z_index: z.z_index ?? idx,
    };
  });
}

export function parseZones(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
  return [];
}

export function loadImageAsDataUri(url) {
  return new Promise(resolve => {
    if (!url) return resolve(null);
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch { resolve(url); }
    };
    img.onerror = () => resolve(url);
    img.src = url;
  });
}

// ── ZoneItemList ──────────────────────────────────────────────────────────────
export function ZoneItemList({ items, zone, tk, accentColor, isAr, font, bodyH, bodyW, isExport }) {
  // Per-attribute color overrides (fall back to theme)
  const colName  = zone.name_color  || tk.text;
  const colDesc  = zone.desc_color  || tk.textFaded;
  const colPrice = zone.price_color || accentColor;
  const cols = Math.max(1, zone.cols || 1);
  const rows = Math.max(1, zone.rows);
  const colGap = cols > 1 ? Math.max(3, bodyW * 0.012) : 0;
  const padH = Math.max(6, bodyW * 0.025);
  const padV = Math.max(3, bodyH * 0.018);
  const rowGap = Math.max(2, bodyH * 0.012);
  const colW = (bodyW - padH * 2 - colGap * (cols - 1)) / cols;
  const rowH = Math.max(14, (bodyH - padV * 2 - rowGap * Math.max(0, rows - 1)) / rows);
  const iconSz = zone.show_photos ? Math.max(14, Math.min(rowH * 0.78, 58)) : 0;
  const phAlpha = zone.show_placeholder === false ? 0 : (zone.placeholder_opacity ?? 100) / 100;
  const rowBg = phAlpha === 1 ? tk.rowBg : phAlpha === 0 ? 'transparent'
    : tk.light ? `rgba(255,255,255,${phAlpha})` : `rgba(255,255,255,${phAlpha * 0.06})`;
  const autoMaxNameFsz = Math.max(7, Math.min(rowH * 0.28, 20));
  const priceFsz = zone.price_font ? parseFloat(zone.price_font) : Math.max(7, Math.min(rowH * 0.32, 22));
  const descFsz  = zone.desc_font  ? parseFloat(zone.desc_font)  : Math.max(5, Math.min(rowH * 0.19, 12));
  const bR = Math.max(3, rowH * 0.10);
  const iconGap = iconSz + Math.max(4, iconSz * 0.22);
  const availNameW = Math.max(30, colW - iconGap - colW * 0.42 - Math.max(5, colW * 0.016) * 2);

  // Pre-compute uniform name font size (min across all items so sizes are equal)
  const uniformNameFsz = zone.name_font ? parseFloat(zone.name_font) : (() => {
    if (!items.length) return autoMaxNameFsz;
    return Math.min(...items.map(item => {
      const n = tr(item.name, item.name_ar, isAr);
      return Math.max(7, Math.min(autoMaxNameFsz, availNameW / (Math.max(3, n.length) * 0.65)));
    }));
  })();

  return (
    <div style={{ width: '100%', height: '100%', padding: `${padV}px ${padH}px`, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)`, gap: `${rowGap}px ${colGap}px`, boxSizing: 'border-box', overflow: 'hidden' }}>
      {items.map((item, i) => {
        const name = tr(item.name, item.name_ar, isAr);
        const desc = tr(item.description, item.description_ar, isAr);
        const imgSrc = isExport ? (item.image_path || item.image_thumb) : (item.image_thumb || item.image_path);
        return (
          <div key={item.id || i} style={{ display: 'flex', alignItems: 'center', gap: zone.show_placeholder === false ? 8 : Math.max(4, iconSz * 0.22), padding: `${Math.max(2, rowH * 0.06)}px ${Math.max(5, colW * 0.016)}px`, background: rowBg, borderRadius: phAlpha > 0 ? bR : 0, borderLeft: phAlpha > 0 ? (isAr ? 'none' : `3px solid ${accentColor}88`) : 'none', borderRight: phAlpha > 0 ? (isAr ? `3px solid ${accentColor}88` : 'none') : 'none', overflow: 'hidden', boxSizing: 'border-box', direction: isAr ? 'rtl' : 'ltr', minHeight: 0 }}>
            {zone.show_photos && (
              <div style={{ width: iconSz, height: iconSz, flexShrink: 0, borderRadius: Math.max(2, bR * 0.5), overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {imgSrc ? (isExport
                  ? <div style={{ width: iconSz, height: iconSz, backgroundImage: `url('${imgSrc}')`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }} />
                  : <img src={imgSrc} alt="" crossOrigin="anonymous" style={{ width: iconSz, height: iconSz, objectFit: 'contain' }} />)
                : (item.icon && item.icon !== '🍽') ? <span style={{ fontSize: iconSz * 0.72, lineHeight: 1 }}>{item.icon}</span> : null}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textAlign: isAr ? 'right' : 'left' }}>
              {zone.show_name !== false && <div style={{ color: colName, fontSize: uniformNameFsz, fontWeight: 600, overflow: 'hidden', whiteSpace: 'nowrap', lineHeight: 1.6, fontFamily: zone.name_font_family || (isAr ? 'Cairo, sans-serif' : (font || 'inherit')) }}>{name}</div>}
              {zone.show_desc && desc && rowH > 26 && <div style={{ color: colDesc, fontSize: descFsz, lineHeight: 1.2, overflow: 'hidden', wordBreak: 'break-word', marginTop: 1, fontFamily: 'Arial, sans-serif', fontWeight: 400 }}>{desc}</div>}
            </div>
            {zone.show_price && (
              <div style={{ flexShrink: 0, minWidth: 0, overflow: 'hidden' }}>
                {item.variants?.length > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: Math.max(2, priceFsz * 0.22), justifyContent: isAr ? 'flex-start' : 'flex-end' }}>
                    {item.variants.map((v, vi) => (
                      <React.Fragment key={vi}>
                        {vi > 0 && <span style={{ color: colDesc, fontWeight: 300, fontSize: priceFsz * 0.65, opacity: 0.5, fontFamily: 'Arial, sans-serif' }}>|</span>}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                          <span style={{ color: colPrice, fontWeight: 800, fontSize: priceFsz, lineHeight: 1.4, whiteSpace: 'nowrap', fontFamily: 'Arial, sans-serif' }}>{fmtP(v.price)}</span>
                          {zone.show_variant_name !== false && <span style={{ color: colDesc, fontFamily: 'Arial, sans-serif', fontWeight: 300, fontSize: Math.max(5, priceFsz * 0.52), lineHeight: 1.1, whiteSpace: 'nowrap', opacity: 0.85 }}>{tr(v.name, v.name_ar, isAr)}</span>}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                ) : <span style={{ color: colPrice, fontWeight: 800, fontSize: priceFsz, whiteSpace: 'nowrap', fontFamily: 'Arial, sans-serif' }}>{fmtP(item.price)}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── ZoneItemGrid ──────────────────────────────────────────────────────────────
export function ZoneItemGrid({ items, zone, tk, accentColor, bgColor, isAr, font, bodyH, bodyW, isExport }) {
  // Per-attribute color overrides (fall back to theme)
  const colName  = zone.name_color  || tk.text;
  const colDesc  = zone.desc_color  || tk.textFaded;
  const colPrice = zone.price_color || accentColor; // doubles as price-badge background
  const cols = Math.max(1, zone.cols), rows = Math.max(1, zone.rows);
  const pad = Math.max(2, Math.min(bodyH * 0.016, 7)), gap = Math.max(2, Math.min(bodyH * 0.013, 6));
  const cellW = (bodyW - pad * 2 - gap * Math.max(0, cols - 1)) / cols;
  const cellH = (bodyH - pad * 2 - gap * Math.max(0, rows - 1)) / rows;
  const cardR = Math.max(6, Math.min(cellH * 0.06, 12));
  const hasAnyDesc = zone.show_desc && items.some(it => (isAr ? it.description_ar : it.description));
  const needInfoBar = (zone.show_name !== false) || zone.show_desc || (!zone.show_photos && zone.show_price);
  // Description-on cards take ~32% of cell height for the info area so
  // descriptions can wrap to 2 lines comfortably; otherwise keep the
  // compact 20% used for name-only layouts.
  const infoFraction = hasAnyDesc ? 0.32 : 0.20;
  const infoH  = !needInfoBar ? 0 : zone.show_photos ? Math.max(20, Math.round(cellH * infoFraction)) : cellH;
  const photoH = zone.show_photos ? cellH - infoH : 0;
  const autoNameFsz = Math.max(8, Math.min(infoH * (hasAnyDesc ? 0.30 : 0.46), 40));
  const nameFsz  = zone.name_font  ? parseFloat(zone.name_font)  : autoNameFsz;
  const priceFsz = zone.price_font ? parseFloat(zone.price_font) : Math.max(8, Math.min(photoH > 0 ? photoH * 0.15 : infoH * 0.44, 40));
  const descFsz  = zone.desc_font  ? parseFloat(zone.desc_font)  : Math.max(5, Math.min(infoH * 0.28, 14));
  const badgeR = Math.max(4, priceFsz * 0.38), badgePV = Math.max(3, priceFsz * 0.28), badgePH = Math.max(6, priceFsz * 0.55);
  const infoPadH = Math.max(6, cellW * 0.05);
  // Auto-pick contrast text color for the price badge based on the (possibly custom) badge bg
  const badgeTextCol = isLightBg(colPrice) ? '#111' : '#fff';

  // Pre-compute uniform fitted name size (min across all items)
  const uniformFittedNameFsz = zone.name_font ? parseFloat(zone.name_font) : (() => {
    if (!items.length) return autoNameFsz;
    return Math.min(...items.map(item => {
      const n = tr(item.name, item.name_ar, isAr);
      return Math.max(8, Math.min(nameFsz, (cellW - infoPadH * 2) / (Math.max(3, n.length) * (isAr ? 0.62 : 0.55))));
    }));
  })();

  return (
    <div style={{ width: '100%', height: '100%', padding: pad, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)`, gap, boxSizing: 'border-box', overflow: 'hidden' }}>
      {items.map((item, i) => {
        const name = tr(item.name, item.name_ar, isAr), desc = tr(item.description, item.description_ar, isAr);
        const imgSrc = isExport ? (item.image_path || item.image_thumb) : (item.image_thumb || item.image_path);
        const hasImg = zone.show_photos && !!imgSrc, hasIcon = zone.show_photos && !imgSrc && item.icon && item.icon !== '🍽';
        const hasVariants = item.variants?.length > 0;
        const simplePrice = String(fmtP(item.price));
        const varNameFsz = Math.max(6, priceFsz * 0.52);
        const varNames = hasVariants ? item.variants.map((v, vi) => (vi > 0 ? ' · ' : '') + tr(v.name, v.name_ar, isAr)).join('') : '';
        return (
          <div key={item.id || i} style={{ borderRadius: zone.show_placeholder === false ? 0 : cardR, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: zone.show_placeholder === false ? 'none' : (tk.light ? '0 2px 10px rgba(0,0,0,0.10)' : '0 2px 10px rgba(0,0,0,0.38)'), border: zone.show_placeholder === false ? 'none' : `1px solid ${accentColor}28` }}>
            {zone.show_photos && (
              <div style={{ height: photoH, flexShrink: 0, position: 'relative', background: tk.imgBg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {hasImg && (isExport
                  ? <div style={{ width: '100%', height: '100%', backgroundImage: `url('${imgSrc}')`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }} />
                  : <img src={imgSrc} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />)}
                {!hasImg && hasIcon && <span style={{ fontSize: Math.min(photoH * 0.5, 60), lineHeight: 1 }}>{item.icon}</span>}
                {!hasImg && !hasIcon && <span style={{ color: accentColor, fontSize: Math.min(photoH * 0.32, 48), fontWeight: 900, opacity: 0.18 }}>{(name || '?').charAt(0).toUpperCase()}</span>}
                {zone.show_price && (
                  <div style={{ position: 'absolute', top: Math.max(4, photoH * 0.05), right: Math.max(4, cellW * 0.04), background: colPrice, borderRadius: badgeR, padding: `${badgePV}px ${badgePH}px`, zIndex: 2, lineHeight: 1, maxWidth: cellW * 0.92, boxSizing: 'border-box' }}>
                    {hasVariants ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {item.variants.map((v, vi) => (
                          <React.Fragment key={vi}>
                            {vi > 0 && <span style={{ color: badgeTextCol, fontWeight: 200, fontSize: priceFsz * 0.7, opacity: 0.65, margin: `0 ${Math.max(3, priceFsz * 0.28)}px` }}>|</span>}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <span style={{ color: badgeTextCol, fontWeight: 800, fontSize: priceFsz, lineHeight: 1.15, whiteSpace: 'nowrap', fontFamily: 'Arial, sans-serif' }}>{fmtP(v.price)}</span>
                              {zone.show_variant_name !== false && <span style={{ color: badgeTextCol, fontFamily: 'Arial, sans-serif', fontWeight: 400, fontSize: varNameFsz, lineHeight: 1.15, opacity: 0.88, whiteSpace: 'nowrap' }}>{tr(v.name, v.name_ar, isAr)}</span>}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: badgeTextCol, fontSize: priceFsz, fontWeight: 800, whiteSpace: 'nowrap', fontFamily: 'Arial, sans-serif' }}>{simplePrice}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {infoH > 0 && (
              <div style={{ flexShrink: 0, height: infoH, borderTop: (zone.show_placeholder === false || !zone.show_photos) ? 'none' : `2px solid ${accentColor}`, background: zone.show_placeholder === false ? 'transparent' : bgColor, display: 'flex', alignItems: 'center', padding: `0 ${infoPadH}px`, direction: isAr ? 'rtl' : 'ltr', gap: 5, overflow: 'hidden', boxSizing: 'border-box' }}>
                <div style={{ flex: 1, minWidth: 0, textAlign: isAr ? 'right' : 'left' }}>
                  {zone.show_name !== false && <div style={{ color: colName, fontSize: uniformFittedNameFsz, fontWeight: 700, fontFamily: zone.name_font_family || (isAr ? 'Cairo, sans-serif' : (font || 'inherit')), lineHeight: 1.25, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{name}</div>}
                  {zone.show_desc && desc && infoH > 36 && <div style={{
                    color: colDesc, fontSize: descFsz, lineHeight: 1.25, marginTop: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    // 2-line clamp instead of single-line truncate so the full
                    // description fits inside the (now larger) info area.
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    fontFamily: 'Arial, sans-serif', fontWeight: 400,
                  }}>{desc}</div>}
                </div>
                {!zone.show_photos && zone.show_price && (
                  <div style={{ flexShrink: 0, background: colPrice, borderRadius: badgeR, padding: `${badgePV}px ${badgePH}px`, lineHeight: 1, maxWidth: cellW * 0.65, boxSizing: 'border-box' }}>
                    {hasVariants ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {item.variants.map((v, vi) => {
                          const fsz = Math.max(8, Math.min(infoH * 0.44, 40));
                          return (
                            <React.Fragment key={vi}>
                              {vi > 0 && <span style={{ color: badgeTextCol, fontWeight: 200, fontSize: fsz * 0.7, opacity: 0.65, margin: `0 ${Math.max(3, fsz * 0.28)}px` }}>|</span>}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span style={{ color: badgeTextCol, fontWeight: 800, fontSize: fsz, lineHeight: 1.15, whiteSpace: 'nowrap', fontFamily: 'Arial, sans-serif' }}>{fmtP(v.price)}</span>
                                {zone.show_variant_name !== false && <span style={{ color: badgeTextCol, fontFamily: 'Arial, sans-serif', fontWeight: 400, fontSize: varNameFsz, lineHeight: 1.15, opacity: 0.88, whiteSpace: 'nowrap' }}>{tr(v.name, v.name_ar, isAr)}</span>}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ color: badgeTextCol, fontSize: Math.max(8, Math.min(infoH * 0.44, 40)), fontWeight: 800, whiteSpace: 'nowrap', fontFamily: 'Arial, sans-serif' }}>{simplePrice}</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── ZonePreview ───────────────────────────────────────────────────────────────
export function ZonePreview({ zone, bgColor, accentColor, lang, tk, zoneW, zoneH, borderRight, borderBottom, font, isExport, suppressBgImage }) {
  const isAr = lang === 'ar';
  const textLayers = Array.isArray(zone.text_layers) ? zone.text_layers : [];
  // Zone header on canvas removed: name is now used only for the editor tab label.
  // For visible on-canvas text, users should add Text Layers.
  const hasLabel = false;
  const labelH = 0;
  const bodyH = zoneH;
  const items = (zone.items || []).slice(0, Math.max(1, zone.cols) * Math.max(1, zone.rows));
  // Phase C-β-2 (2026-05-16) — when the compose pipeline plans to composite
  // the zone's bg_image as a separate ffmpeg input (so animated WebPs animate),
  // SlideBuilder renders the static base with suppressBgImage=true. The zone
  // body still renders (items, borders, dim, item text) but the bg image
  // square is skipped, leaving a transparent rectangle that the animated
  // input fills underneath in the final compose. We split:
  //   hasBgImage   — raw setting, drives item theme + dim layer (kept on)
  //   renderBgImage — actual <img>/<div> render (skipped when suppressed)
  const hasBgImage   = !!zone.bg_image;
  const renderBgImage = hasBgImage && !suppressBgImage;
  const hasBg = hasBgImage;
  const dimOpacity  = hasBg ? (zone.bg_overlay ?? 50) / 100 : 0;
  const dimColor    = tk.light ? `rgba(255,255,255,${dimOpacity})` : `rgba(0,0,0,${dimOpacity})`;
  // When zone has a bg image, make item row/card backgrounds semi-transparent (theme-aware)
  const bgRowBg   = tk.light ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.52)';
  const bgCardBg  = tk.light ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.52)';
  const itemTk    = hasBg ? { ...tk, rowBg: bgRowBg, imgBg: tk.light ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.30)' } : tk;
  const itemBgColor = hasBg ? bgCardBg : bgColor;
  const hasBgColor = !!zone.bg_color;
  const bgColorOpacity = (zone.bg_color_opacity ?? 100) / 100;
  const customBorderWidth = parseFloat(zone.border_width) || 0;
  const customBorderRadius = parseFloat(zone.border_radius) || 0;
  const customBorderColor = zone.border_color || '';
  const hasCustomBorder = customBorderWidth > 0 && customBorderColor;
  const wrapperStyle = {
    position: 'relative', width: zoneW, height: zoneH, overflow: 'hidden',
    display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
    borderRadius: customBorderRadius || 0,
  };
  if (hasCustomBorder) {
    wrapperStyle.border = `${customBorderWidth}px solid ${customBorderColor}`;
  } else {
    wrapperStyle.borderRight = borderRight ? `1px solid ${accentColor}44` : 'none';
    wrapperStyle.borderBottom = borderBottom ? `1px solid ${accentColor}44` : 'none';
  }
  return (
    <div style={wrapperStyle}>
      {/* Solid background color (below image) */}
      {hasBgColor && (
        <div style={{ position: 'absolute', inset: 0, background: zone.bg_color, opacity: bgColorOpacity, zIndex: 0, pointerEvents: 'none' }} />
      )}
      {/* Background image — render only when not being supplied separately
          by the ffmpeg compose (Phase C-β-2 animated zone bgs). */}
      {renderBgImage && (isExport
        ? <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url('${zone.bg_image}')`, backgroundSize: '100% 100%', backgroundPosition: 'center', zIndex: 0 }} />
        : <img src={zone.bg_image} alt="" crossOrigin="anonymous" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'fill', zIndex: 0, display: 'block' }} />
      )}
      {/* Dim overlay */}
      {hasBg && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: dimColor, zIndex: 1, pointerEvents: 'none' }} />}
      {/* Items */}
      <div style={{ position: 'relative', zIndex: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {(zone.display_mode === 'list' || (!zone.display_mode && zone.cols === 1 && zone.rows > 1))
          ? <ZoneItemList items={items} zone={zone} tk={itemTk} accentColor={accentColor} isAr={isAr} font={font} bodyH={bodyH} bodyW={zoneW} isExport={isExport} />
          : <ZoneItemGrid items={items} zone={zone} tk={itemTk} accentColor={accentColor} bgColor={itemBgColor} isAr={isAr} font={font} bodyH={bodyH} bodyW={zoneW} isExport={isExport} />}
      </div>
      {/* Free text layers (rendered on top of items) */}
      {textLayers.map((layer, li) => (
        <TextLayerDiv key={layer.id || li} layer={layer} font={font} isAr={isAr} keyId={layer.id || li} />
      ))}
    </div>
  );
}

// ── SlidePreview ──────────────────────────────────────────────────────────────
export function SlidePreview({ restaurant, zones, zone_cols, zone_rows, zone_mode, bgColor, accentColor, orientation, lang, isExport, slideBgImage, slideBgOverlay, widthPx, heightPx, textLayers, slideKind, videoPreviewPath, autoDarken, currentTime, slideDuration, onlyLayerId, excludeAnimated, excludeZoneBgImages }) {
  // Phase C-β compose helpers — these props let the save flow render either
  // (a) the static overlay with animated TEXT layers AND animated zones HIDDEN
  //     (excludeAnimated=true) so the saved MP4's "base" layer is everything
  //     that doesn't move; or
  // (b) a SINGLE animated layer or zone in isolation (onlyLayerId=<id>) for
  //     the per-overlay passes that ffmpeg fades in/out over time.
  //
  // 2026-05-17 — animated zones now leave the base and get their own isolated
  // render → fade+enable in ffmpeg, exact same path as animated text layers.
  // (The earlier multi-input animated-WebP-zone-bg pipeline was reverted; a
  // static frame of the zone fading in/out is the practical compromise.)
  const filterTextLayer = (l) => {
    if (onlyLayerId)     return l.id === onlyLayerId;
    if (excludeAnimated) return !hasAnyAnimation(l, slideDuration);
    return true;
  };
  const filterZone = (z) => {
    if (onlyLayerId)     return z.id === onlyLayerId;
    if (excludeAnimated) return !hasAnyAnimation(z, slideDuration);
    return true;
  };
  // When isolating a single layer, drop the bg fill / image / video so the
  // exported PNG is transparent everywhere except where the layer sits.
  const isIsolatedLayerRender = !!onlyLayerId;
  // Phase C MVP — only drive animations when a playhead is actively provided
  // by the SlideBuilder pane. isExport callers leave currentTime undefined so
  // the canvas snapshot captures every layer at its natural state.
  const playT = (!isExport && currentTime != null) ? currentTime : null;
  // Per-slide resolution override → preview is half of output. Falls back to
  // legacy DESIGN[orientation] (960×540 / 540×960) when no override given.
  const dim = (widthPx && heightPx)
    ? { w: widthPx / 2, h: heightPx / 2 }
    : (DESIGN[orientation] || DESIGN.H);
  const font = restaurant?.font || 'Cairo';
  const tk = makeTokens(bgColor || '#ffffff', accentColor);
  const zC = Math.max(1, zone_cols || 1), zR = Math.max(1, zone_rows || 1);
  const isFree = zone_mode === 'free';
  // Phase A (2026-05-16): a video-kind slide with an encoded preview file
  // renders an HTML5 <video> at z-index 0 and suppresses the image bg layer
  // (the video IS the bg). The auto-darken slider becomes a black overlay at
  // z-index 1, mirroring slideBgOverlay's role for image slides. isExport
  // skips both the video and the darken — the canvas capture is the
  // transparent-overlay-only output that Phase B's compose pipeline expects.
  // Phase B (2026-05-16): image bg is always suppressed when kind === 'video',
  // including during export — the compose pipeline overlays the canvas on
  // top of the encoded bg.mp4, so any baked-in image bg would double up.
  const isVideoKind = slideKind === 'video';
  const hasVideoBg  = isVideoKind && !!videoPreviewPath && !isExport;
  const hasSlideBg  = !!slideBgImage && !isVideoKind;
  const slideDimOpacity = hasSlideBg ? (slideBgOverlay ?? 50) / 100 : 0;
  const slideDimColor = tk.light ? `rgba(255,255,255,${slideDimOpacity})` : `rgba(0,0,0,${slideDimOpacity})`;
  const darkenOpacity = hasVideoBg ? Math.max(0, Math.min(80, autoDarken || 0)) / 100 : 0;
  const sortedZones = isFree
    ? [...(zones || [])].sort((a, b) => (a.z_index || 0) - (b.z_index || 0))
    : (zones || []);
  // Empty bg_color → transparent slide. In editor preview, show a checkered pattern so the
  // user sees that transparent areas are truly transparent (not white). On JPEG export,
  // html2canvas paints over the wrapper with its own white default — we keep this here too.
  // For a video-kind slide with the video element rendering, the checkered
  // pattern is irrelevant (video covers it) — we suppress it so a brief
  // <video> load doesn't flash checkered.
  const isTransparentBg = !bgColor || bgColor === 'transparent' || bgColor === '#0000' || bgColor === 'none';
  const checkered = {
    backgroundColor: '#fff',
    backgroundImage: 'linear-gradient(45deg,#ddd 25%,transparent 25%),linear-gradient(-45deg,#ddd 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ddd 75%),linear-gradient(-45deg,transparent 75%,#ddd 75%)',
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0',
  };
  // Phase B (2026-05-16) — the wrapper's background colour was getting baked
  // into the overlay WebP during html2canvas capture, then the compose step
  // pasted that opaque rectangle over the video and hid it. For video slides
  // we always want a transparent wrapper during export, regardless of the
  // slide's bg_color (the encoded MP4 IS the bg).
  let slideBgStyle;
  if (isIsolatedLayerRender) {
    // Single-layer export — bg must be transparent so ffmpeg can overlay
    // just the layer pixels on top of the running compose stream.
    slideBgStyle = { background: 'transparent' };
  } else if (isVideoKind && isExport) {
    slideBgStyle = { background: 'transparent' };
  } else if (hasVideoBg) {
    slideBgStyle = { background: 'transparent' };
  } else if (isTransparentBg) {
    slideBgStyle = isExport ? { background: 'transparent' } : checkered;
  } else {
    slideBgStyle = { background: bgColor };
  }
  return (
    <div
      // 2026-05-16: tag the root so SlideBuilder can distinguish a click on
      // the slide background (no zone / no text) for the "unfold Slide
      // section" + right-click-paste UX. Zone/text overlays sit above with
      // their own handlers — pointer-events still work as before.
      data-slide-background="true"
      style={{ position: 'relative', width: dim.w, height: dim.h, ...slideBgStyle, fontFamily: `'${font}', 'Cairo', sans-serif`, flexShrink: 0, overflow: 'hidden' }}>
      {hasVideoBg && (
        <video src={videoPreviewPath} autoPlay loop muted playsInline
          // key forces React to swap the element when the URL (incl. cache-bust
          // token) changes — without this, replacing a video leaves the old
          // src buffered.
          key={videoPreviewPath}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', zIndex: 0, display: 'block', background: '#000', pointerEvents: 'none' }}
        />
      )}
      {hasVideoBg && darkenOpacity > 0 && (
        <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${darkenOpacity})`, zIndex: 1, pointerEvents: 'none' }} />
      )}
      {hasSlideBg && (isExport
        ? <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${slideBgImage}')`, backgroundSize: '100% 100%', backgroundPosition: 'center', zIndex: 0 }} />
        : <img src={slideBgImage} alt="" crossOrigin="anonymous" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', zIndex: 0, display: 'block' }} />
      )}
      {hasSlideBg && <div style={{ position: 'absolute', inset: 0, background: slideDimColor, zIndex: 1, pointerEvents: 'none' }} />}
      {isFree ? (
        <div style={{ position: 'relative', zIndex: 2, width: '100%', height: '100%' }}>
          {sortedZones.filter(filterZone).map((zone, idx) => {
            const zw = Math.max(1, zone.w || dim.w);
            const zh = Math.max(1, zone.h || dim.h);
            // Phase C: per-zone animation. Same model as text layers — outside
            // the time window the zone is hidden; inside, opacity + CSS effect
            // play. isExport leaves playT=null so the canvas snapshot includes
            // every zone in its natural state.
            const zAnim = playT != null
              ? computeLayerAnim(zone, playT, slideDuration)
              : null;
            if (zAnim && !zAnim.visible) return null;
            const zStyle = {
              position: 'absolute',
              left: zone.x || 0,
              top: zone.y || 0,
              width: zw,
              height: zh,
              zIndex: zone.z_index || 0,
            };
            if (zAnim) {
              zStyle.opacity = zAnim.opacity;
              zStyle['--layer-opacity'] = zAnim.opacity;
              if (zAnim.animation) zStyle.animation = zAnim.animation;
            }
            return (
              <div key={zone.id || idx} style={zStyle}>
                <ZonePreview zone={zone} bgColor={bgColor} accentColor={accentColor} lang={lang} tk={tk}
                  zoneW={zw} zoneH={zh} borderRight={false} borderBottom={false} font={font} isExport={isExport}
                  suppressBgImage={excludeZoneBgImages} />
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ position: 'relative', zIndex: 2, width: '100%', height: '100%', display: 'grid', gridTemplateColumns: `repeat(${zC}, 1fr)`, gridTemplateRows: `repeat(${zR}, 1fr)` }}>
          {sortedZones.map((zone, idx) => (
            <ZonePreview key={zone.id || idx} zone={zone} bgColor={bgColor} accentColor={accentColor} lang={lang} tk={tk}
              zoneW={dim.w / zC} zoneH={dim.h / zR} borderRight={idx % zC < zC - 1} borderBottom={Math.floor(idx / zC) < zR - 1} font={font} isExport={isExport}
              suppressBgImage={excludeZoneBgImages} />
          ))}
        </div>
      )}
      {/* Slide-level text layers — rendered above all zones */}
      {Array.isArray(textLayers) && textLayers.length > 0 && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
          {[...textLayers]
            .filter(filterTextLayer)
            .sort((a, b) => (a.z_index || 0) - (b.z_index || 0))
            .map((layer, li) => (
              <TextLayerDiv key={layer.id || li} layer={layer} font={font} isAr={lang === 'ar'} keyId={layer.id || li}
                currentTime={playT} slideDuration={slideDuration} />
            ))}
        </div>
      )}
    </div>
  );
}

// ── Door Sign bindings ────────────────────────────────────────────────────────
// A door-sign TEMPLATE is an ordinary slide whose text layers / zone images are
// bound to named fields. One template + N sets of values = N doors, each
// rendered and pushed to its own screen.
//
// The field schema lives on brand_slides.sign_fields:
//   [{ key, label, label_ar, type: 'text'|'image'|'status', default, options }]
// where `default` doubles as the DUMMY value shown while designing — the slide
// builder itself needs no changes, the designer just types a realistic
// placeholder into the layer and binds it here.
//
// Two ways to bind, both honoured:
//   1. layer.binding = 'doctor_name'    → the whole layer text is the value
//   2. "Room {{room_no}} · {{floor}}"   → inline substitution anywhere in text
// Images bind through zone.bg_image_binding / slide.slide_bg_image_binding.
// A zone's fill can bind too: zone.bg_color_binding takes the colour of the
// selected status option, so a solid state badge recolours with the state
// while its label stays legible white.

/** Map of key → the field's dummy/default value. Used for design-time preview. */
export function signFieldDefaults(fields) {
  const out = {};
  for (const f of (fields || [])) {
    if (!f || !f.key) continue;
    out[f.key] = f.default != null ? f.default : '';
  }
  return out;
}

// Resolve one field key to its display string. A 'status' field stores the
// option's `value`; what belongs on the door is the option's label.
function resolveFieldValue(key, values, fieldMap, isAr) {
  const field = fieldMap[key];
  let raw = values && Object.prototype.hasOwnProperty.call(values, key) ? values[key] : undefined;
  if (raw === undefined || raw === null || raw === '') raw = field?.default ?? '';
  if (field?.type === 'status') {
    const opt = (field.options || []).find(o => o.value === raw);
    if (opt) return isAr ? (opt.label_ar || opt.label || raw) : (opt.label || opt.label_ar || raw);
  }
  return String(raw ?? '');
}

// Status fields can also drive the layer's colour, so "Busy" turns the text red
// without needing a second bound layer.
function resolveFieldColor(key, values, fieldMap) {
  const field = fieldMap[key];
  if (!field || field.type !== 'status') return null;
  const raw = (values && values[key]) || field.default || '';
  return (field.options || []).find(o => o.value === raw)?.color || null;
}

const MUSTACHE_RE = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

function substituteText(text, values, fieldMap, isAr) {
  if (typeof text !== 'string' || text.indexOf('{{') < 0) return text;
  return text.replace(MUSTACHE_RE, (m, key) =>
    (fieldMap[key] ? resolveFieldValue(key, values, fieldMap, isAr) : m));
}

function bindLayer(layer, values, fieldMap, isAr) {
  if (!layer) return layer;
  let out = layer;
  const key = layer.binding;
  if (key && fieldMap[key]) {
    const txt = resolveFieldValue(key, values, fieldMap, isAr);
    // An empty value falls back to the layer's own text so the designer's
    // dummy stays visible instead of the layer vanishing mid-design.
    out = { ...out, text: txt !== '' ? txt : layer.text, text_en: undefined, text_ar: undefined };
    if (layer.binding_color) {
      const c = resolveFieldColor(key, values, fieldMap);
      if (c) out.color = c;
    }
  } else {
    const t   = substituteText(layer.text,    values, fieldMap, isAr);
    const ten = substituteText(layer.text_en, values, fieldMap, isAr);
    const tar = substituteText(layer.text_ar, values, fieldMap, isAr);
    if (t !== layer.text || ten !== layer.text_en || tar !== layer.text_ar) {
      out = { ...out, text: t, text_en: ten, text_ar: tar };
    }
  }
  return out;
}

/**
 * Return a copy of `slide` with every binding resolved against `values`.
 * Pure — safe to call on each keystroke for the live preview, and again right
 * before renderSlideToCanvas for the published image.
 *
 * @param {object} slide   the template row (zones/text_layers parsed or raw)
 * @param {object} values  { field_key: value } for this one door
 * @param {Array}  fields  the template's sign_fields schema
 * @param {string} lang    'ar' | 'en' — picks which status label to show
 */
export function applySignBindings(slide, values, fields, lang = 'ar') {
  if (!slide) return slide;
  const schema = Array.isArray(fields) ? fields : parseZones(fields);
  if (!schema.length) return slide;
  const fieldMap = {};
  for (const f of schema) if (f && f.key) fieldMap[f.key] = f;
  const isAr = lang === 'ar';
  const vals = values || {};

  const bindImage = (key, current) => {
    if (!key || !fieldMap[key]) return current;
    const v = resolveFieldValue(key, vals, fieldMap, isAr);
    return v || current;
  };
  const bindColor = (key, current) => {
    if (!key || !fieldMap[key]) return current;
    return resolveFieldColor(key, vals, fieldMap) || current;
  };

  // The published page lays a list out with flexbox; canvas has no flex, so the
  // same rows are turned into positioned text here. Rows share the box height
  // and columns divide its width by percentage — the same arithmetic the browser
  // does, so the preview matches the glass.
  const listLayers = [];
  for (const z of parseZones(slide.zones)) {
    const lf = z.list_binding && fieldMap[z.list_binding];
    if (!lf || lf.type !== 'list') continue;
    const cols = (Array.isArray(lf.columns) && lf.columns.length) ? lf.columns : [{ key: 'text', width: 100 }];
    let rows = vals[lf.key];
    if (!Array.isArray(rows) || !rows.length) rows = Array.isArray(lf.default) ? lf.default : [];
    if (lf.max_rows > 0) rows = rows.slice(0, lf.max_rows);
    if (!rows.length) continue;
    const pad = Number(z.list_pad) || 0;
    const gap = Number(z.list_gap) || 0;
    const innerW = (Number(z.w) || 0) - pad * 2;
    const innerH = (Number(z.h) || 0) - pad * 2;
    const rowH = (innerH - gap * (rows.length - 1)) / rows.length;
    rows.forEach((row, ri) => {
      if (!row || typeof row !== 'object') return;
      let cx = (Number(z.x) || 0) + pad;
      cols.forEach((c, ci) => {
        const cw = innerW * ((Number(c.width) || 50) / 100);
        listLayers.push({
          id: `${z.id || 'z'}_l${ri}_${ci}`,
          text: row[c.key] == null ? '' : String(row[c.key]),
          x: cx, y: (Number(z.y) || 0) + pad + ri * (rowH + gap),
          w: cw, h: rowH,
          font_size: Number(c.size) || 15,
          font_weight: c.weight || 'normal',
          font_family: c.font_family || '',
          color: c.color || '#12213A',
          align: c.align === 'end' ? 'right' : c.align === 'center' ? 'center' : 'left',
          opacity: 100, z_index: 40 + ri,
        });
        cx += cw;
      });
    });
  }

  return {
    ...slide,
    slide_bg_image: bindImage(slide.slide_bg_image_binding, slide.slide_bg_image),
    zones: parseZones(slide.zones).map(z => ({
      ...z,
      bg_image: bindImage(z.bg_image_binding, z.bg_image),
      bg_color: bindColor(z.bg_color_binding, z.bg_color),
      text_layers: Array.isArray(z.text_layers)
        ? z.text_layers.map(l => bindLayer(l, vals, fieldMap, isAr))
        : z.text_layers,
    })),
    text_layers: [
      ...parseZones(slide.text_layers).map(l => bindLayer(l, vals, fieldMap, isAr)),
      ...listLayers,
    ],
  };
}

/** Every binding key a template references — used to flag unbound fields. */
export function collectBindings(slide) {
  const keys = new Set();
  const scan = (layers) => {
    for (const l of (layers || [])) {
      if (!l) continue;
      if (l.binding) keys.add(l.binding);
      for (const f of [l.text, l.text_en, l.text_ar]) {
        if (typeof f !== 'string') continue;
        let m; const re = new RegExp(MUSTACHE_RE.source, 'g');
        while ((m = re.exec(f))) keys.add(m[1]);
      }
    }
  };
  scan(parseZones(slide?.text_layers));
  for (const z of parseZones(slide?.zones)) {
    scan(z.text_layers);
    if (z.bg_image_binding) keys.add(z.bg_image_binding);
    if (z.bg_color_binding) keys.add(z.bg_color_binding);
  }
  if (slide?.slide_bg_image_binding) keys.add(slide.slide_bg_image_binding);
  return [...keys];
}

// ── Standalone render-to-canvas ───────────────────────────────────────────────
// menuData: { allItems: [...], categories: [...] }  (from /api/menuboard/menu-data/:brandId)
// restaurant: brand object  (from /api/brands/:id)
export async function renderSlideToCanvas(slideData, orientation, menuData, restaurant, lang = 'en', renderOpts = {}) {
  // Pre-shape Arabic text into Presentation Forms-B so html2canvas doesn't have
  // to do shaping (which it gets wrong). See preShape* helpers above.
  slideData  = preShapeSlideData(slideData);
  menuData   = preShapeMenuData(menuData);
  restaurant = preShapeRestaurant(restaurant);

  function resolveItems(itemIds) {
    if (!menuData || !Array.isArray(itemIds)) return [];
    return itemIds.map(id => {
      const item = menuData.allItems?.find(i => i.id === id);
      if (!item) return null;
      const cat = menuData.categories?.find(c => c.id === item.category_id);
      return { ...item, cat_name: cat?.name || '', cat_name_ar: cat?.name_ar || '' };
    }).filter(Boolean);
  }

  // Per-slide custom resolution → preview is half output; html2canvas scale:2
  // produces the exact requested pixel size. Legacy fallback unchanged.
  const d = (slideData.width_px && slideData.height_px)
    ? { w: slideData.width_px / 2, h: slideData.height_px / 2 }
    : (DESIGN[orientation] || DESIGN.H);
  const zc = slideData.zone_cols || 1, zr = slideData.zone_rows || 1;
  const isFree = slideData.zone_mode === 'free';
  // Free mode: zone count is independent of cols/rows — don't pad/trim
  const baseZones = isFree ? parseZones(slideData.zones) : adjustZones(parseZones(slideData.zones), zc, zr);
  const zones = baseZones.map(z => ({ ...z, items: resolveItems(z.item_ids || []) }));

  const rawSlideBgImage = slideData.slide_bg_image || '';

  // Pre-load all images (item photos + zone backgrounds + slide background) as data URIs (avoids CORS in html2canvas)
  const urlSet = [...new Set([
    ...zones.flatMap(z => z.items || []).map(i => i.image_path || i.image_thumb),
    ...zones.map(z => z.bg_image).filter(Boolean),
    rawSlideBgImage || null,
  ].filter(Boolean))];
  const uriMap = {};
  await Promise.all(urlSet.map(async url => { uriMap[url] = await loadImageAsDataUri(url); }));

  const zonesHiRes = zones.map(z => ({
    ...z,
    bg_image: (z.bg_image && uriMap[z.bg_image]) || z.bg_image,
    items: (z.items || []).map(item => {
      const url = item.image_path || item.image_thumb;
      const uri = url && uriMap[url];
      return uri ? { ...item, image_path: uri, image_thumb: uri } : item;
    }),
  }));

  const slideBgImageHiRes = (rawSlideBgImage && uriMap[rawSlideBgImage]) || rawSlideBgImage;

  // Pre-load every font referenced in the slide so html2canvas captures with the right typeface,
  // not a fallback. The @font-face rules are already injected globally by useFonts(), but the
  // browser only fetches a font lazily — so we trigger explicit loads here and wait for them.
  try {
    const families = new Set();
    const firstFamily = (s) => String(s || '').split(',')[0].replace(/^['"]|['"]$/g, '').trim();
    for (const z of zones) {
      if (z.name_font_family) families.add(firstFamily(z.name_font_family));
      if (Array.isArray(z.text_layers)) {
        for (const l of z.text_layers) if (l && l.font_family) families.add(firstFamily(l.font_family));
      }
    }
    if (Array.isArray(slideData.text_layers)) {
      for (const l of slideData.text_layers) if (l && l.font_family) families.add(firstFamily(l.font_family));
    }
    if (document.fonts && typeof document.fonts.load === 'function') {
      await Promise.all([...families].filter(Boolean).map(f =>
        document.fonts.load(`16px "${f}"`).catch(() => null)
      ));
      if (document.fonts.ready) await document.fonts.ready;
    }
  } catch (_) { /* font preload best-effort */ }

  // Render off-screen
  const tempDiv = document.createElement('div');
  tempDiv.style.cssText = `position:fixed;left:-${d.w + 100}px;top:0;width:${d.w}px;height:${d.h}px;overflow:hidden;`;
  document.body.appendChild(tempDiv);
  const root = createRoot(tempDiv);
  await new Promise(resolve => {
    root.render(
      <SlidePreview
        restaurant={restaurant}
        zones={zonesHiRes}
        zone_cols={zc}
        zone_rows={zr}
        zone_mode={slideData.zone_mode || 'grid'}
        bgColor={slideData.bg_color || ''}
        accentColor={slideData.accent_color || '#F59E0B'}
        orientation={orientation}
        lang={lang}
        isExport={true}
        slideBgImage={slideBgImageHiRes}
        slideBgOverlay={slideData.slide_bg_overlay ?? 50}
        widthPx={slideData.width_px}
        heightPx={slideData.height_px}
        textLayers={parseZones(slideData.text_layers)}
        // Phase B (2026-05-16): mark kind on export so SlidePreview
        // suppresses the image bg layer for video slides. Compose pipeline
        // overlays this canvas on the encoded bg.mp4 — image bg would double up.
        slideKind={slideData.slide_kind}
        // Phase C-β (2026-05-16) — per-layer compose-time animation. The
        // save flow renders the static overlay with excludeAnimated=true (so
        // animated layers are absent from the base) and then renders each
        // animated layer alone with onlyLayerId so ffmpeg can fade-and-gate
        // them separately on top of the base.
        onlyLayerId={renderOpts.onlyLayerId}
        excludeAnimated={renderOpts.excludeAnimated}
        excludeZoneBgImages={renderOpts.excludeZoneBgImages}
        slideDuration={slideData.duration_sec || 15}
      />
    );
    setTimeout(resolve, 800);
  });
  try {
    // Phase 5: when no bg_color (empty / 'transparent' / 'none'), let the canvas stay
    // transparent so the resulting WebP carries an alpha channel. Otherwise paint with
    // the chosen bg color (legacy behaviour).
    // Phase B (2026-05-16): video slides always export transparent — the bg
    // is the encoded MP4 and the compose pipeline overlays this canvas on top.
    // Phase C-β: isolated-layer renders are always transparent — ffmpeg
    // overlays them onto the compose stream, so any bg pixels would double up.
    const bgRaw = (slideData.bg_color || '').trim().toLowerCase();
    const isTransparent = renderOpts.onlyLayerId
      || slideData.slide_kind === 'video'
      || !bgRaw || bgRaw === 'transparent' || bgRaw === 'none' || bgRaw === '#0000';
    return await html2canvas(tempDiv, {
      scale: 2, useCORS: true, allowTaint: false,
      backgroundColor: isTransparent ? null : slideData.bg_color,
      width: d.w, height: d.h, windowWidth: d.w, windowHeight: d.h,
    });
  } finally {
    root.unmount();
    document.body.removeChild(tempDiv);
  }
}
